import React from "react";
import { Link } from "react-router-dom";
import { Truck, Package2, Thermometer, Beaker } from "lucide-react";
import { IMG } from "@/lib/site";
import { useProduct } from "@/context/ProductContext";
import { formatGBP } from "@/lib/formatGBP";

const BADGES = [
  { icon: Truck, label: "UK Tracked Delivery" },
  { icon: Package2, label: "Compact Design" },
  { icon: Thermometer, label: "Temperature-Controlled Storage" },
  { icon: Beaker, label: "Peptigen Labs Sister Brand" },
];

const CONFIDENCE = [
  "Pre-order open",
  "Ships July 2026",
  "14-day returns from delivery",
];

/**
 * Hero — Apple-style static product showcase.
 *
 * One image. No animation. No framing decorations behind or beneath it. The
 * image edges are feathered to transparency by the .hero-product-mask radial
 * gradient so the rectangular boundary is invisible against the page; the
 * product itself stays fully sharp inside the inner mask region.
 */
export default function Hero() {
  const { product, loading, error } = useProduct();

  return (
    <section
      id="hero"
      data-testid="section-hero"
      className="relative overflow-hidden"
      style={{ background: "linear-gradient(180deg, #ffffff 0%, #F5FAFD 100%)" }}
    >
      <link rel="preload" as="image" href={IMG.hero} />

      <div className="container-pc pt-6 pb-16 lg:pt-12 lg:pb-28">
        <div className="grid gap-10 lg:gap-12 xl:gap-16 items-center lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
          {/* Static product stage — just the product on the page. The image
           *  edges are feathered to transparency by the mask class so the
           *  rectangular boundary is invisible against the hero gradient. */}
          <div
            className="order-1 lg:order-2 relative xl:-mr-8 2xl:-mr-16"
            data-testid="hero-image-stage"
          >
            <div className="relative z-10">
              <div
                className="relative w-full mx-auto aspect-[3/2] xl:aspect-[16/10] max-h-[55vh] sm:max-h-[60vh] lg:max-h-none xl:max-w-none"
              >
                <img
                  src={IMG.hero}
                  alt="PeptiChill compact peptide cooler with Peptigen Labs logo on the front face and digital LCD showing 2°C"
                  className="absolute inset-0 w-full h-full object-contain blend-product hero-product-mask drop-shadow-[0_30px_60px_rgba(11,37,69,0.10)]"
                  fetchPriority="high"
                  width="1600"
                  height="1000"
                  data-testid="hero-product-image"
                />
              </div>
            </div>
          </div>

          {/* Copy column */}
          <div className="order-2 lg:order-1">
            <p className="eyebrow">PeptiChill by Peptigen Labs</p>

            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[64px] font-bold leading-[1.04] text-navy tracking-tightest">
              Precision cooling for peptide storage.
            </h1>

            <p className="mt-6 text-base lg:text-lg text-muted leading-relaxed max-w-xl">
              A compact, clinical-grade cooler fridge with a front-mounted digital LCD and
              hinged top lid — designed for research-use peptides and temperature-sensitive
              laboratory materials.
            </p>

            <ul
              className="mt-6 flex flex-wrap gap-x-5 gap-y-1 text-sm text-navy/85"
              data-testid="hero-confidence-row"
            >
              {CONFIDENCE.map((c) => (
                <li key={c} className="inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan inline-block" aria-hidden="true" />
                  {c}
                </li>
              ))}
            </ul>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/product"
                data-testid="hero-cta-shop"
                className="inline-flex items-center justify-center rounded-full bg-navy text-white px-7 py-3 text-sm font-semibold tracking-wide hover:brightness-110 transition shadow-card"
              >
                Pre-order Now
              </Link>
              <a
                href="#specs"
                data-testid="hero-cta-specs"
                className="inline-flex items-center justify-center rounded-full border border-navy text-navy px-7 py-3 text-sm font-semibold tracking-wide hover:bg-ice transition"
              >
                View Specifications
              </a>
            </div>

            <p
              className="mt-3 text-sm text-navy flex flex-wrap items-center gap-x-2 gap-y-1"
              data-testid="hero-micro-trust"
            >
              {loading && (
                <span
                  className="inline-block w-16 h-4 rounded bg-navy/10 align-middle"
                  data-testid="hero-price-skeleton"
                  aria-hidden="true"
                />
              )}
              {!loading && product && (
                <span className="font-medium" data-testid="hero-price">
                  {formatGBP(product.price_pennies)} pre-order price
                </span>
              )}
              {!loading && !product && error && (
                <span className="text-muted text-xs italic" data-testid="hero-price-unavailable">
                  Price unavailable — please refresh
                </span>
              )}
              <span className="text-cyan" aria-hidden="true">·</span>
              <span className="font-normal">Ships July 2026</span>
              <span className="text-cyan" aria-hidden="true">·</span>
              <span className="font-normal">UK tracked delivery</span>
            </p>

            <ul className="mt-10 flex flex-wrap gap-2.5" data-testid="hero-trust-badges">
              {BADGES.map(({ icon: Icon, label }) => (
                <li key={label} className="pill">
                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-navy text-white">
                    <Icon size={11} strokeWidth={2.4} />
                  </span>
                  {label}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
